import React, {Fragment, useContext, useEffect, useState} from "react";
import {DropdownListFormNotSelectedStringId, DropdownListItem} from "../../components/forms/dropdownListForm";
import SuggestionForm from "../../components/forms/suggestionForm";
import {Group} from "../patients/dataForGroup";
import {searchGroupListByName} from "../patients/groupService";
import Spinner from "../../components/spinner/spinner";
import {ProviderContext} from "../providerContext";
import {getPatientsCountByGroup} from "./campaignService";
import Button from "../../components/button/button";
import {ButtonStatus} from "../../components/button/enum";
import {PromptContext} from "../promptContext";
import {CampaignSetting} from "./dataForDesignCampaign";
import CustomModal from "../../components/modal/customModal";
import PatientPreviewList from "../patients/patientPreviewList";
import CampaignFilterPage from "./campaignFilter";
import {CampaignBigType} from "./dataForActiveCampaign";
import {ActionType, PresentationStyle} from "../../util/enum";
import NewGroup, {MaxMatchingPatientCount} from "../patients/newGroup";

export enum GroupSource {
    custom,
    healthRegistry,
}

interface IProps {
    setting: CampaignSetting,
    onContinue: (_setting: CampaignSetting) => void,
    onBack: () => void,
}

function GroupSelection(props: IProps) {
    const {onBack, onContinue } = props
    const [setting, setSetting] = useState<CampaignSetting>(props.setting)
    const [originalGroupList, setOriginalGroupList] = useState<Array<Group>>([])
    const [groupList, setGroupList] = useState<Array<Group>>([])
    const [isShowSpinner, setIsShowSpinner] = useState<boolean>(false)
    const [searchKeyword, setSearchKeyword] = useState<string>(props.setting.groupName || "")

    const [isFilterValid, setIsFilterValid] = useState<boolean>(true)
    const [showPatientPreviewModal, setShowPatientPreviewModal] = useState<boolean>(false)
    const [showNewGroupModal, setShowNewGroupModal] = useState<boolean>(false)

    const provider = useContext(ProviderContext)
    const {showError, showAlert, showInfo} = useContext(PromptContext)

    useEffect(() => {
        setting.campaignBigType === CampaignBigType.regularCampaign &&
            getAllGroupList(true)
    }, [])

    const getAllGroupList = (alertForNone: boolean) => {
        setIsShowSpinner(true)
        searchGroupListByName(provider.mEmployeeID, "", (groupList) => {
            setIsShowSpinner(false)
            if (groupList.length) {
                setOriginalGroupList(groupList)
                initializeDefaultGroup(groupList)
            } else {
                alertForNone && showAlert(
                    "No group list to select",
                    "Do you want to create a new group now?",
                    {title: "Create", onClick: () => {
                        setShowNewGroupModal(true)
                    }},
                    {title: "Cancel", onClick: undefined}
                )
            }
        }, (error) => {
            setIsShowSpinner(false)
            showError(error)
        })
    }

    useEffect(() => {
        if (searchKeyword) {
            // throttleCb(setting.groupName)
            let list = originalGroupList.filter((item) => item.name.toLowerCase().includes(searchKeyword.toLowerCase()));
            setGroupList(list)
        } else {
            setGroupList(originalGroupList)
        }
    }, [searchKeyword, originalGroupList])

    useEffect(() => {
        if (!setting.groupId) {
            return
        }
        setIsShowSpinner(true)
        getPatientsCountByGroup(setting.groupId, (count) => {
            setIsShowSpinner(false)
            setSetting({
                ...setting,
                matchingCount: count,
            })
        }, (error) => {
            setIsShowSpinner(false)
            setSetting({
                ...setting,
                matchingCount: 0,
            })
            showError(error)
        })
    }, [setting.groupId])

    const initializeDefaultGroup = (groupList: Array<Group>) => {
        let selectedGroupId = sessionStorage.getItem("selected_group_id")
        if (selectedGroupId) {
            let selectedGroup = groupList.find((item) => item.id === selectedGroupId)
            selectedGroup && setSearchKeyword(selectedGroup.name)
            selectedGroup && setSetting({
                ...setting,
                groupId: selectedGroupId,
                groupName: selectedGroup.name,
            })
            sessionStorage.removeItem("selected_group_id")
        }
    }

    const getDataForGroupList = (): Array<DropdownListItem> => {
        return groupList.map((group) => {
            return {
                name: group.name,
                id: group.id,
            }
        })
    }

    const $sectionTitle = (title: string) => {
        return (<p className={"font-semibold text-base text-gray-1"}>{title}</p>)
    }

    const title = setting.campaignBigType !== CampaignBigType.regularCampaign ? "Filters" : "Group"
    const subTitle = setting.campaignBigType !== CampaignBigType.regularCampaign ? "Add filters for your automated campaign. Leave blank if no preferences." : "Select one or more groups that you want to send the campaign to."
    const $titleView = (
        <Fragment>
            <p className={"font-bold text-2xl"}>{title}</p>
            <p className={"text-gray-2 mt-2 font-base text-base"}>{subTitle}</p>
            <div className={"w-full h-px bg-gray-300 mt-4"}/>
        </Fragment>
    )

    const onChangeGroupName = (name: string) => {
        setSearchKeyword(name.trim())
    }

    const onSelectGroupId = (id: any) => {
        if (setting.groupId !== id) {
            let selectedGroup = groupList.find((item) => item.id === id)
            selectedGroup && setSetting({
                ...setting,
                groupId: id,
                groupName: selectedGroup.name,
            })
        }
    }

    const previewPatientList = () => {
        if (!setting.matchingCount) {
            return
        }
        if (setting.matchingCount <= MaxMatchingPatientCount) {
            setShowPatientPreviewModal(true)
        } else {
            showInfo("Cannot preview more than 1,000 patients.")
        }
    }

    const errMsgForSuggestionForm = setting.groupId === DropdownListFormNotSelectedStringId ? "Please type to select a existing group" : ""
    const $sectionGroup = (
        <div className={"flex flex-col space-y-2"}>
            {$sectionTitle("Select a group")}
            <div className={"mb-4"}>
                <SuggestionForm errMsg={errMsgForSuggestionForm} value={searchKeyword} placeholder={"Search for a group"} title={"Group Name"} data={getDataForGroupList()} onChangeText={onChangeGroupName} onSelect={onSelectGroupId}/>
                <p className={"text-sm text-gray-3 italic my-1"}>Campaign will be sent to this group. Add a new group or edit existing ones from ‘patients’.</p>
            </div>
        </div>
    )

    const $createNewGroup = (
        <Button status={ButtonStatus.link} onClick={() => {
            // history.push(RoutePath.newGroup)
            setShowNewGroupModal(true)
        }} >
        + Create New
        </Button>
    )

    const $matchPatientsCountView = setting.campaignBigType === CampaignBigType.regularCampaign ? (
        <div className={"w-full flex flex-col items-end"}>
            <div className={"w-full h-px bg-gray-200 mt-6"}/>
            <p className={"text-base text-gray-1 inline-flex my-6"}>
            Matching
                <button disabled={!setting.matchingCount} onClick={previewPatientList} className={`mx-2 text-base ${setting.matchingCount > 0 ? "text-theme-blue" : "text-red-500"} font-bold`}>{setting.matchingCount}</button>
            Patients
            </p>
        </div>) : null

    const onClickContinue = () => {
        onContinue && onContinue(setting)
    }

    const $backButton = (
        <Button status={ButtonStatus.light} onClick={() => {
            setSetting(props.setting)
            onBack && onBack()
        }} >
            Back
        </Button>
    )
    const $continueButtonForRegularCampaign = (
        <Button status={setting.matchingCount > 0 ? ButtonStatus.primary : ButtonStatus.disabled} onClick={onClickContinue} >
            Continue
        </Button>
    )

    const $continueButtonForAutomatedCampaign = (
        <Button status={isFilterValid ? ButtonStatus.primary : ButtonStatus.disabled} onClick={onClickContinue} >
            Continue
        </Button>
    )

    const $continueButton = setting.campaignBigType !== CampaignBigType.regularCampaign ? $continueButtonForAutomatedCampaign : $continueButtonForRegularCampaign
    const $footerButton = (
        <div className={"w-full flex flex-row items-center justify-between mt-6"}>
            {$backButton}
            {$continueButton}
        </div>
    )

    const $patientPreviewModal = (
        <CustomModal isOpen={showPatientPreviewModal}>
            <PatientPreviewList
                groupId={setting.groupId}
                actionType={ActionType.view}
                onClose={() => {
                    setShowPatientPreviewModal(false)
                }}
            />
        </CustomModal>
    )

    const $spinner = <Spinner isOpen={isShowSpinner} />
    const $groupSelectionForRegularCampaign = setting.campaignBigType === CampaignBigType.regularCampaign ? (
        <div className={"flex flex-col space-y-2"}>
            {$sectionGroup}
            <div className={"w-max"}>
                {$createNewGroup}
            </div>
        </div>
    ) : null

    const $filterPageForAutomatedCampaign = setting.campaignBigType !== CampaignBigType.regularCampaign ? (
        <CampaignFilterPage onChange={(filter, isValid) => {
            setSetting({
                ...setting,
                filter: filter,
            })
            setIsFilterValid(isValid)
        }} setting={setting}/>
    ) : null

    const $sectionView = (
        <div className={"mt-6"}>
            {$groupSelectionForRegularCampaign}
            {$filterPageForAutomatedCampaign}
        </div>
    )

    const $newGroupModal = (
        <CustomModal
            isOpen={showNewGroupModal}
        >
            <NewGroup
                presentationStyle={PresentationStyle.modal}
                onDismiss={() => {
                    setShowNewGroupModal(false)
                    getAllGroupList(false)
                }}
            />
        </CustomModal>
    )

    return (
        <div className={"w-full flex flex-col p-6 lg:p-10 bg-white shadow-lg rounded-lg "}>
            {$titleView}
            {$sectionView}
            {$matchPatientsCountView}
            {$footerButton}
            {$patientPreviewModal}
            {$newGroupModal}
            {$spinner}
        </div>
    )
}

export default GroupSelection
